import { z } from 'zod';

import { apiRequest, apiRequestRaw } from '@/api/client';
import { setMaintenance } from '@/features/system/maintenance';

/** Schemas mirroring `app/api/system.py` and `app/api/v1/meta.py`. */

export const healthSchema = z.object({
  status: z.literal('ok'),
  service: z.string(),
  version: z.string(),
  environment: z.string(),
});

export const readinessSchema = z.object({
  status: z.enum(['ready', 'degraded']),
  dependencies: z.object({
    database: z.enum(['up', 'down']),
    redis: z.enum(['up', 'down']),
  }),
});

export const platformMetaSchema = z.object({
  name: z.string(),
  version: z.string(),
  api_version: z.string(),
  environment: z.string(),
  default_currency: z.string(),
  default_page_size: z.number(),
  max_page_size: z.number(),
});

export type Health = z.infer<typeof healthSchema>;
export type Readiness = z.infer<typeof readinessSchema>;
export type PlatformMeta = z.infer<typeof platformMetaSchema>;

export function fetchHealth(): Promise<Health> {
  return apiRequestRaw('/health', healthSchema);
}

export function fetchReadiness(): Promise<Readiness> {
  return apiRequestRaw('/ready', readinessSchema);
}

export function fetchPlatformMeta(): Promise<PlatformMeta> {
  return apiRequest('/meta', platformMetaSchema);
}

// --- Platform notices ------------------------------------------------------

/**
 * Banners the platform wants shown before anyone starts work.
 *
 * Unauthenticated on purpose: a shopper has no account, and "we are in
 * maintenance" is exactly the thing that must still be sayable while the rest
 * of the platform is refusing work. Empty in normal operation.
 */
export const NOTICE_AUDIENCES = ['DASHBOARD', 'STOREFRONT'] as const;
export type NoticeAudience = (typeof NOTICE_AUDIENCES)[number];

/** Keys the backend reserves for maintenance windows; anything else is a flag or an announcement. */
export const MAINTENANCE_ACTIVE = 'MAINTENANCE_ACTIVE';
export const MAINTENANCE_UPCOMING = 'MAINTENANCE_UPCOMING';

export const platformNoticeSchema = z.object({
  key: z.string(),
  message: z.string(),
  level: z.enum(['INFO', 'WARNING', 'CRITICAL']),
  /** Maintenance notices only: which kind of window. */
  mode: z.enum(['READ_ONLY', 'FULL']).nullish(),
  /** Maintenance notices only: when it starts (the countdown) and roughly ends. */
  starts_at: z.string().nullish(),
  ends_at: z.string().nullish(),
});

export type PlatformNotice = z.infer<typeof platformNoticeSchema>;

/**
 * What the platform wants shown on one surface. The audience matters: a
 * shopper must not be shown a banner written for merchants.
 */
export function fetchPlatformNotices(audience: NoticeAudience) {
  return apiRequest(`/meta/notices?audience=${audience}`, z.array(platformNoticeSchema), {
    auth: 'none',
  });
}

export function platformNoticesKey(audience: NoticeAudience) {
  return ['platform-notices', audience] as const;
}

/**
 * Keep the maintenance store in step with a notices answer: an active window
 * is recorded, and its absence clears whatever was recorded before.
 */
export function syncMaintenanceFromNotices(notices: readonly PlatformNotice[]): void {
  const active = notices.find((notice) => notice.key === MAINTENANCE_ACTIVE);
  setMaintenance(
    active
      ? { mode: active.mode ?? 'FULL', endsAt: active.ends_at ?? null, message: active.message }
      : null,
  );
}

// --- Paused capabilities ---------------------------------------------------

/**
 * A capability the platform has switched off for this business - a kill
 * switch on the platform's Flags page. Mirrors `app/api/v1/flags.py`.
 */
export const pausedFlagSchema = z.object({
  key: z.string(),
  message: z.string(),
  /** An estimate for the paused screen; null when nobody has given one. */
  expected_back_at: z.string().nullish(),
});

export type PausedFlag = z.infer<typeof pausedFlagSchema>;

export const PAUSED_FLAGS_KEY = ['flags', 'paused'] as const;

/** Empty in normal operation. Per-business exceptions are already applied. */
export function fetchPausedFlags() {
  return apiRequest('/flags/paused', z.array(pausedFlagSchema));
}
